import * as Sentry from "@sentry/react";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";

import { Spinner } from "@/components/Spinner";
import { createQueryClient, queryClient as browserQueryClient } from "@/lib/api";
import { routeTree } from "@/routeTree.gen";

/** Read a 0-1 sample rate, falling back if it is missing or unusable.
 *
 * A malformed value must not become 1.0 -- full sampling is what emptied the Sentry budget
 * and took error reporting down with it. Callers pass `import.meta.env.VITE_...` directly
 * because Vite only substitutes statically-written keys. */
function sampleRate(raw: unknown, fallback: number): number {
  if (raw === undefined || raw === null || raw === "") return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

// Set up a Router instance
export function getRouter() {
  // The server calls this once per request, and the SSR integration dehydrates the client's
  // whole cache into the page. A client shared across requests would put every earlier
  // visitor's queries into each response and grow without bound, as nothing expires them
  // on the server. The browser keeps the one shared client that code outside React uses.
  const queryClient =
    typeof window === "undefined" ? createQueryClient() : browserQueryClient;

  const router = createRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: "intent",

    context: { queryClient },
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
    defaultPreloadDelay: 150,

    defaultPendingComponent: () => (
      <div className="h-screen w-full flex items-center justify-center">
        <Spinner delay={750} />
      </div>
    ),
  });

  setupRouterSsrQueryIntegration({
    queryClient,
    router,
  });

  // Browser only. On the server every request would init a client that is never released,
  // and through the router integration it keeps that request's router and data alive.
  if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
    Sentry.init({
      dsn: "https://3330a231b301a3baee75d27b2c2c74d9@o4508375021518849.ingest.us.sentry.io/4508375027417088",
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.replayIntegration(),
      ],
      ignoreErrors: [
        // Network errors you might want to filter
        "Network request failed",
        "Failed to fetch",
        "NetworkError",
        /ResizeObserver loop limit exceeded/,
      ],
      // Spans and replays bill against a shared dollar budget that errors also draw on
      // for overage. The backend services tracing at 1.0 exhausted that budget and
      // stopped Sentry accepting errors org-wide, this project included -- 57 of its
      // 1.7k errors made it in that week. The frontend is nowhere near the biggest
      // contributor, but full sampling here has no more justification than it did there.
      tracesSampleRate: sampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
      tracePropagationTargets: [/^\//, /^https:\/\/api\.graze\.social\/app/],
      // Session replays ran 8x over the plan's monthly allowance. Replays attached to an
      // actual error are the ones anybody opens, so those stay on and ambient recording
      // of healthy sessions goes off.
      replaysSessionSampleRate: sampleRate(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE, 0),
      replaysOnErrorSampleRate: 1.0,
    });
  }

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}

// When we make a deploy the user might have references to old files
// which will trigger a preload error. When this happens we want to
// reload the page to ensure they get the latest version.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", () => {
    window.location.reload();
  });
}
